import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, Alert, AlertTitle } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { AuthLayout } from "../../../components/layout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
} from "../../../components/Form/FormContext";
import { selectAuthLoading } from "../../../store/selectors/authSelectors";
import { login as loginAction } from "../../../store/actions/authActions";
import { ROUTES, COOKIE_KEYS } from "../../../constants";
import { store } from "../../../store/store";
import { getCookie, setCookie } from "../../../utils/cookieUtils";
import { validatePassword } from "../../../utils/passwordValidation";
import { getPostLoginRoute } from "../../../utils/routeUtils";
import { useApiErrorHandling } from "../../../hooks";
import { ValidationResult } from "../../../hooks/useFormValidation";
import { FormFieldValue } from "../../../components/Form/FormContext";
import {
  LoginPageContainer,
  LoginFormContainer,
  LoginTitle,
  LoginSubtitle,
  PolicyLinksContainer,
  PolicyLink,
} from "./LoginPage.styled";

type TenantBlockStatus = "suspended" | null;

interface LoginFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
  tenantBlockStatus: TenantBlockStatus;
}

const LoginForm: React.FC<LoginFormProps> = ({
  apiErrors,
  fieldErrors,
  tenantBlockStatus,
}) => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectAuthLoading);
  const { validationResult } = useFormContext();
  const isBlocked = tenantBlockStatus !== null;

  // Disable submit when client-side validation reports errors
  const isFormValid =
    !!validationResult?.isValid &&
    Array.isArray(validationResult?.errorMessages) &&
    validationResult.errorMessages.length === 0;

  return (
    <>
      {tenantBlockStatus === "suspended" && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {t("login.tenantSuspended.title", "Account Suspended")}
          </AlertTitle>
          {t(
            "login.tenantSuspended.message",
            "Your organization account has been temporarily suspended. You are currently unable to log in. If you believe this is an error contact your organization manager.",
          )}
        </Alert>
      )}

      <FormTextInput
        name="email"
        label={t("login.email")}
        type="email"
        placeholder={t("login.emailPlaceholder")}
        required
        disabled={isBlocked}
      />

      <FormTextInput
        name="password"
        label={t("login.password")}
        type="password"
        placeholder={t("login.passwordPlaceholder")}
        required
        disabled={isBlocked}
      />

      <Box sx={{ mt: "14px" }}>
        <Link to={ROUTES.FORGOT_PASSWORD}>{t("login.forgotPassword")}</Link>
      </Box>

      {!isBlocked && (
        <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />
      )}

      <Box sx={{ mt: "32px" }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading || !isFormValid || isBlocked}
        >
          {isLoading ? "Logging in..." : t("login.loginButton")}
        </Button>
      </Box>
    </>
  );
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Incorrect email or password. Please try again.",
    });
  const [tenantBlockStatus, setTenantBlockStatus] =
    useState<TenantBlockStatus>(null);
  // Pre-fill email if the user has already gone through the first step.
  // Memoized with [] so the object reference stays stable across re-renders
  // (e.g. when isLoading flips). Without this, every render produces a new
  // object reference which causes useFormValidation's useEffect to fire and
  // reset formData to { email } only — wiping the password mid-submission.
  const savedFormData = useMemo<FormDataType | undefined>(() => {
    const savedEmail = getCookie(COOKIE_KEYS.EMAIL_HINT);
    return savedEmail ? { email: savedEmail } : undefined;
  }, []);

  const validationRules = useMemo(
    () => ({
      email: {
        required: true,
        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Please enter a valid email address.",
      },
      password: {
        required: true,
        custom: (value: FormFieldValue) => {
          const strValue = String(value || "");
          if (!strValue) {
            return t("register.validation.passwordRequired");
          }
          const error = validatePassword(strValue, t);
          return error ? t("login.incorrectCredentials") : null;
        },
      },
    }),
    [t],
  );

  const handleSubmit = useCallback(
    async (formData: FormDataType, validationResult: ValidationResult) => {
      // Block submit strictly based on validationRules outcome
      const hasClientErrors =
        !validationResult?.isValid ||
        (Array.isArray(validationResult?.errorMessages) &&
          validationResult.errorMessages.length > 0);
      if (hasClientErrors) return;

      clearErrors();
      setTenantBlockStatus(null);
      try {
        const result = await dispatch(
          loginAction({
            email: String(formData.email),
            password: String(formData.password),
          }),
        ).unwrap();

        // Save only the email for subsequent steps — never persist the password
        setCookie(COOKIE_KEYS.EMAIL_HINT, String(formData.email), 0);

        if (result.isTrustedDevice) {
          // Trusted device — skip 2FA, user is already in the Redux store
          const roleId = store.getState().auth.user?.roleId;
          navigate(getPostLoginRoute(roleId));
        } else {
          // Navigate to verification page for 2FA
          navigate(`${ROUTES.VERIFICATION}?redirect=${ROUTES.TWO_FACTOR_AUTH}`);
        }
      } catch (errors: unknown) {
        const errorDetail =
          (errors as { detail?: string })?.detail?.toLowerCase() ?? "";

        console.log(errorDetail);
        console.log(errorDetail.includes("inactive"));
        if (errorDetail.includes("inactive")) {
          setTenantBlockStatus("suspended");
        } else {
          setTenantBlockStatus(null);
          handleApiError(errors);
        }
      }
    },
    [dispatch, navigate, clearErrors, handleApiError],
  );

  return (
    <AuthLayout>
      <LoginPageContainer>
        <LoginFormContainer>
          <LoginTitle>{t("login.title")}</LoginTitle>

          <LoginSubtitle>{t("login.subtitle")}</LoginSubtitle>

          <Form
            validationRules={validationRules}
            onSubmit={handleSubmit}
            initialData={savedFormData}
            externalErrors={fieldErrors}
            showFieldErrors={false}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
            }}
          >
            <LoginForm
              apiErrors={apiErrors}
              fieldErrors={fieldErrors}
              tenantBlockStatus={tenantBlockStatus}
            />
          </Form>
          <PolicyLinksContainer>
            <PolicyLink
              href={ROUTES.TERMS}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.termsOfUse")}
            </PolicyLink>
            <PolicyLink
              href={ROUTES.PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.privacyPolicy")}
            </PolicyLink>
            <PolicyLink
              href={ROUTES.SMS_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.smsMessagingPolicy")}
            </PolicyLink>
          </PolicyLinksContainer>
        </LoginFormContainer>
      </LoginPageContainer>
    </AuthLayout>
  );
};

export default LoginPage;
